/**
 * Menu icon picker — the icon the running application's shell draws for a
 * navigation entry (folders included) in every layout: menus, tabs, the Dock,
 * the ribbon, breadcrumbs and the screen title.
 *
 * The choices are the allow-listed set in
 * `packages/page-runtime/src/shell/nav/navIcons.json` (the same list the
 * backend validates against), grouped as that file groups them. Empty = the
 * entry type's default icon.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { NAV_ICON_GROUPS, NavIcon, isNavIcon } from '@twasta/page-runtime/shell'

interface Props {
  value?: string
  objType: string
  onChange: (icon: string) => void
}

export default function NavIconPicker({ value, objType, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const current = isNavIcon(value) ? value : ''

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey) }
  }, [open])

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return NAV_ICON_GROUPS
      .map((g) => ({ ...g, icons: q ? g.icons.filter((n) => n.includes(q)) : g.icons }))
      .filter((g) => g.icons.length > 0)
  }, [filter])

  const pick = (name: string) => { onChange(name); setOpen(false) }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
        Menu icon
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-tw-action="nav-icon-picker"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 180, justifyContent: 'space-between' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <NavIcon node={{ icon: current, obj_type: objType }} size={18} color="var(--color-accent)" />
            {current || 'Default for this type'}
          </span>
          <ChevronDown size={14} />
        </button>
        {current && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange('')}
            title="Use the default icon for this type" aria-label="Clear menu icon">
            <X size={14} />
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
        Shown in the running application&apos;s menus, tabs, dock, ribbon and screen title.
      </div>
      {open && (
        <div role="dialog" aria-label="Choose a menu icon" style={{
          position: 'absolute', zIndex: 50, top: 'calc(100% + 4px)', left: 0, width: 'min(460px, 90vw)',
          maxHeight: 380, overflow: 'auto', padding: 10, borderRadius: 10,
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
        }}>
          <input
            autoFocus
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter icons…"
            aria-label="Filter icons"
            style={{ width: '100%', marginBottom: 8 }}
          />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => pick('')}
            style={{ marginBottom: 6 }}>
            <NavIcon node={{ obj_type: objType }} size={16} /> Default for this type
          </button>
          {groups.map((g) => (
            <div key={g.id} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', margin: '4px 2px' }}>
                {g.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: 4 }}>
                {g.icons.map((name) => (
                  <button key={name} type="button" onClick={() => pick(name)} title={name} aria-label={name}
                    aria-pressed={name === current} data-tw-action={`nav-icon:${name}`}
                    style={{
                      height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 8, cursor: 'pointer', color: 'var(--color-text)',
                      background: name === current ? 'var(--color-accent-weak)' : 'transparent',
                      border: `1px solid ${name === current ? 'var(--color-accent)' : 'transparent'}`,
                    }}>
                    <NavIcon name={name} size={18} />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', padding: 6 }}>No icon matches “{filter}”.</div>
          )}
        </div>
      )}
    </div>
  )
}
